class BlogArticlesGrid extends HTMLElement {
  constructor() {
    super();
    this.selectors = {
      toolbar: "[data-blog-toolbar]",
      filterButtons: ["[data-blog-filters] button"],
      searchInput: "[data-blog-search]",
      grid: "[data-articles-grid]",
      empty: "[data-articles-empty]",
      paginateWrapper: "[data-paginate]",
      loadMoreBtn: "[data-load-more]",
    };
  }

  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.blogUrl = this.dataset.blogUrl;
    this.domNodes = queryDomNodes(this.selectors, this);

    this.activeTag = "";
    this.activeTagUrl = this.blogUrl;
    this.searchTerm = "";
    this.currentPage = 1;
    this.totalPages = this.domNodes.paginateWrapper
      ? Number(this.domNodes.paginateWrapper.dataset.totalPages)
      : 1;
    this.currentTarget = this.buildTarget();
    this.infiniteLoadingObserver = null;

    this.restoreStateFromUrl();
    this.bindFilters();
    this.bindSearch();
    this.bindLoadMore();

    this.onPopState = this.onPopState.bind(this);
    window.addEventListener("popstate", this.onPopState);
  }

  disconnectedCallback() {
    window.removeEventListener("popstate", this.onPopState);
  }

  restoreStateFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const tag = params.get("tag") || "";
    const q = params.get("q") || "";
    if (!tag && !q) return;

    this.searchTerm = q;
    if (this.domNodes.searchInput) this.domNodes.searchInput.value = q;

    if (tag && this.domNodes.filterButtons) {
      const match = this.domNodes.filterButtons.find(
        (btn) => btn.dataset.filterTag === tag,
      );
      if (match) {
        this.activeTag = tag;
        this.activeTagUrl = match.dataset.filterUrl;
        this.setActivePill(match);
      }
    }

    this.applyFilters(false);
  }

  bindFilters() {
    if (!this.domNodes.filterButtons || !this.domNodes.filterButtons.length)
      return;
    addEventDelegate({
      context: this,
      selector: "[data-blog-filters] button",
      handler: (e, target) => {
        e.preventDefault();
        this.activeTag = target.dataset.filterTag;
        this.activeTagUrl = target.dataset.filterUrl;
        this.setActivePill(target);
        this.applyFilters();
      },
    });
  }

  setActivePill(target) {
    this.domNodes.filterButtons.forEach((btn) =>
      btn.classList.remove("is-active"),
    );
    target.classList.add("is-active");
  }

  bindSearch() {
    if (!this.domNodes.searchInput) return;
    const debouncedSearch = debounce((value) => {
      this.searchTerm = value.trim();
      this.applyFilters();
    }, 400);
    this.domNodes.searchInput.addEventListener("input", (e) => {
      debouncedSearch(e.target.value);
    });
  }

  bindLoadMore() {
    if (!this.domNodes.loadMoreBtn) return;
    const paginateType = this.domNodes.paginateWrapper
      ? this.domNodes.paginateWrapper.dataset.paginate
      : "load-more";

    addEventDelegate({
      context: this,
      selector: this.selectors.loadMoreBtn,
      handler: (e) => {
        e.preventDefault();
        this.loadMore();
      },
    });

    if (paginateType === "infinite") {
      this.infiniteLoadingObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.intersectionRatio === 1) this.loadMore();
          });
        },
        { threshold: 1 },
      );
      this.infiniteLoadingObserver.observe(this.domNodes.loadMoreBtn);
    }
  }

  buildTarget() {
    if (this.searchTerm && this.activeTag) {
      return {
        url: (window.Shopify && Shopify.routes.search_url) || "/search",
        params: {
          type: "article",
          q: `${this.searchTerm} tag:"${this.activeTag}"`,
        },
        sourceSelector: "[data-products-grid]",
        sectionId: null,
      };
    }
    if (this.searchTerm) {
      return {
        url: (window.Shopify && Shopify.routes.search_url) || "/search",
        params: { type: "article", q: this.searchTerm },
        sourceSelector: "[data-products-grid]",
        sectionId: null,
      };
    }
    if (this.activeTag) {
      return {
        url: this.activeTagUrl,
        params: {},
        sourceSelector: "[data-articles-grid]",
        sectionId: this.sectionId,
      };
    }
    return {
      url: this.blogUrl,
      params: {},
      sourceSelector: "[data-articles-grid]",
      sectionId: this.sectionId,
    };
  }

  applyFilters(updateUrlHash = true) {
    this.currentTarget = this.buildTarget();
    this.currentPage = 1;
    this.toggleLoading(true);
    this.fetchAndRender(this.currentTarget, { append: false })
      .catch(() => this.showNoResults())
      .finally(() => {
        this.toggleLoading(false);
        if (updateUrlHash) this.updateUrlHash();
      });
  }

  loadMore() {
    const nextPage = this.currentPage + 1;
    if (nextPage > this.totalPages) return;
    this.toggleButtonLoading(true);
    this.fetchAndRender(this.currentTarget, {
      append: true,
      page: nextPage,
    })
      .catch((err) => console.error("Failed to load more articles.", err))
      .finally(() => this.toggleButtonLoading(false));
  }

  showNoResults() {
    if (this.domNodes.grid) {
      this.domNodes.grid.innerHTML = "";
      this.domNodes.grid.classList.add("hidden");
    }
    if (this.domNodes.empty) this.domNodes.empty.classList.remove("hidden");
    if (this.domNodes.paginateWrapper) {
      this.domNodes.paginateWrapper.classList.add("hidden");
    }
  }

  fetchAndRender(target, { append = false, page = 1 } = {}) {
    const params = { ...target.params };
    if (page > 1) params.page = page;

    const absoluteUrl = new URL(target.url, window.location.origin).href;
    // The search results section id is per-template and unknowable from this
    // page's Liquid context, so fetch the full /search page instead of using
    // the Section Rendering API shortcut for that path.
    const request = target.sectionId
      ? fetchSection(target.sectionId, { url: absoluteUrl, params })
      : this.fetchFullPage(absoluteUrl, params);

    return request.then((doc) => {
      const sourceNode = doc.querySelector(target.sourceSelector);
      const nodes = sourceNode ? sourceNode.querySelectorAll(".f-column") : [];

      if (!append && this.domNodes.grid) this.domNodes.grid.innerHTML = "";
      if (this.domNodes.grid) {
        nodes.forEach((node) => this.domNodes.grid.appendChild(node));
      }
      if (!append) {
        const hasResults = this.domNodes.grid
          ? this.domNodes.grid.children.length > 0
          : nodes.length > 0;
        if (this.domNodes.grid) {
          this.domNodes.grid.classList.toggle("hidden", !hasResults);
        }
        if (this.domNodes.empty) {
          this.domNodes.empty.classList.toggle("hidden", hasResults);
        }
      }

      const paginateNode = doc.querySelector(this.selectors.paginateWrapper);
      this.totalPages = paginateNode
        ? Number(paginateNode.dataset.totalPages) || 1
        : 1;
      this.currentPage = page;
      this.toggleLoadMoreVisibility();
    });
  }

  fetchFullPage(url, params) {
    const target = new URL(url);
    Object.entries(params).forEach(([k, v]) => target.searchParams.set(k, v));
    return fetch(target, {
      headers: { "X-Requested-With": "XMLHttpRequest" },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Failed to load: ${target}`);
        return response.text();
      })
      .then((html) => new DOMParser().parseFromString(html, "text/html"));
  }

  toggleLoadMoreVisibility() {
    if (!this.domNodes.paginateWrapper) return;
    const hasMore = this.currentPage < this.totalPages;
    this.domNodes.paginateWrapper.classList.toggle("hidden", !hasMore);
    if (this.infiniteLoadingObserver && this.domNodes.loadMoreBtn) {
      if (hasMore) {
        this.infiniteLoadingObserver.observe(this.domNodes.loadMoreBtn);
      } else {
        this.infiniteLoadingObserver.unobserve(this.domNodes.loadMoreBtn);
      }
    }
  }

  toggleLoading(loading) {
    if (this.domNodes.grid) {
      this.domNodes.grid.toggleAttribute("data-loading", loading);
    }
  }

  toggleButtonLoading(loading) {
    if (this.domNodes.loadMoreBtn) {
      this.domNodes.loadMoreBtn.classList.toggle("btn--loading", loading);
    }
  }

  updateUrlHash() {
    const params = new URLSearchParams();
    if (this.activeTag) params.set("tag", this.activeTag);
    if (this.searchTerm) params.set("q", this.searchTerm);
    const query = params.toString();
    const url = `${window.location.pathname}${query ? "?" + query : ""}`;
    history.pushState({ tag: this.activeTag, q: this.searchTerm }, "", url);
  }

  onPopState(event) {
    const state = event.state || {};
    this.searchTerm = state.q || "";
    this.activeTag = state.tag || "";
    this.activeTagUrl = this.blogUrl;
    if (this.domNodes.searchInput) {
      this.domNodes.searchInput.value = this.searchTerm;
    }
    if (this.domNodes.filterButtons) {
      const match = this.domNodes.filterButtons.find(
        (btn) => btn.dataset.filterTag === this.activeTag,
      );
      this.setActivePill(
        match ||
          this.domNodes.filterButtons.find((btn) => !btn.dataset.filterTag),
      );
      if (match) this.activeTagUrl = match.dataset.filterUrl;
    }
    this.applyFilters(false);
  }
}

customElements.define("blog-articles-grid", BlogArticlesGrid);
